import React from "react";
import { useLanguage } from "../../context/LanguageContext";

interface LanguageSwitcherProps {
  className?: string;
  isMobile?: boolean;
}

// Crisp Vector Flag: Indonesia (🔴 / ⚪)
const IndonesiaFlag: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.4)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    }}
  >
    <rect width="20" height="10" fill="#E11D48" />
    <rect y="10" width="20" height="10" fill="#FFFFFF" />
  </svg>
);

// Crisp Vector Flag: United Kingdom / English (🇬🇧)
const UKFlag: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.4)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      overflow: "hidden",
    }}
  >
    <rect width="60" height="60" fill="#012169" />
    <path d="M0 0L60 60M60 0L0 60" stroke="#FFFFFF" strokeWidth="10" />
    <path d="M0 0L60 60M60 0L0 60" stroke="#C8102E" strokeWidth="6" />
    <path d="M30 0V60M0 30H60" stroke="#FFFFFF" strokeWidth="16" />
    <path d="M30 0V60M0 30H60" stroke="#C8102E" strokeWidth="10" />
  </svg>
);

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = "",
  isMobile = false,
}) => {
  const { language, setLanguage } = useLanguage();

  return (
    <div
      className={`pca-language-pill-switcher ${isMobile ? "pca-mobile-mode" : ""} ${className}`}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: "rgba(10, 16, 28, 0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(0, 255, 151, 0.3)",
        borderRadius: "30px",
        padding: "3px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.1)",
        userSelect: "none",
        zIndex: 20,
      }}
    >
      {/* Animated Sliding Highlight Pill */}
      <div
        style={{
          position: "absolute",
          top: "3px",
          bottom: "3px",
          left: language === "id" ? "3px" : "calc(50%)",
          width: "calc(50% - 3px)",
          backgroundColor: "#00FF97",
          borderRadius: "24px",
          boxShadow: "0 0 12px rgba(0, 255, 151, 0.5)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      {/* Button ID */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setLanguage("id");
        }}
        aria-label="Ubah Bahasa ke Bahasa Indonesia"
        style={{
          position: "relative",
          zIndex: 2,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
          padding: isMobile ? "6px 12px" : "6px 14px",
          borderRadius: "24px",
          border: "none",
          backgroundColor: "transparent",
          color: language === "id" ? "#00020F" : "rgba(255, 255, 255, 0.8)",
          fontSize: isMobile ? "12px" : "12px",
          fontWeight: 700,
          cursor: "pointer",
          transition: "color 0.25s ease",
          letterSpacing: "0.5px",
          lineHeight: 1,
        }}
      >
        <IndonesiaFlag />
        <span>ID</span>
      </button>

      {/* Button EN */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setLanguage("en");
        }}
        aria-label="Switch Language to English"
        style={{
          position: "relative",
          zIndex: 2,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
          padding: isMobile ? "6px 12px" : "6px 14px",
          borderRadius: "24px",
          border: "none",
          backgroundColor: "transparent",
          color: language === "en" ? "#00020F" : "rgba(255, 255, 255, 0.8)",
          fontSize: isMobile ? "12px" : "12px",
          fontWeight: 700,
          cursor: "pointer",
          transition: "color 0.25s ease",
          letterSpacing: "0.5px",
          lineHeight: 1,
        }}
      >
        <UKFlag />
        <span>EN</span>
      </button>
    </div>
  );
};

export default LanguageSwitcher;
